/**
 * RTK Query API for Driver operations
 *
 * This API replaces the deprecated `redux/reducers/drivers/driversSlice`.
 * All driver operations should use these hooks instead of Redux actions.
 *
 * @example
 * // Fetching drivers
 * const { data, isLoading } = useGetDriversQuery({ page: 1, limit: 20 });
 *
 * @example
 * // Searching drivers
 * const { data } = useGetDriversQuery({
 *   page: 1,
 *   limit: 20,
 *   search: true,
 *   searchTerm: 'john'
 * });
 *
 * @example
 * // Creating a driver
 * const [createDriver, { isLoading }] = useCreateDriverMutation();
 * await createDriver({
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   type: 'driver',
 *   ...otherData
 * });
 *
 * @example
 * // Updating a driver
 * const [updateDriver] = useUpdateDriverMutation();
 * await updateDriver({ id: driverId, data: { firstName: 'Jane' } });
 */
import { api } from "../../api";

const driversApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get paginated list of drivers with optional search
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (1-based)
     * @param {number} params.limit - Items per page
     * @param {boolean} params.search - Enable search
     * @param {string} params.searchTerm - Search term for driver name, email, or phone
     * @returns Paginated driver list with metadata
     */
    getDrivers: builder.query({
      query: ({ page = 1, limit = 20, search, searchTerm, ...params }) => {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          ...params,
        });

        // Add search parameter if search is enabled and searchTerm is provided
        if (search && searchTerm && searchTerm.trim()) {
          queryParams.append("search", searchTerm);
        }

        return `drivers?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.drivers
          ? [
              ...result.drivers.map(({ _id }) => ({
                type: "Drivers",
                id: _id,
              })),
              { type: "Drivers", id: "LIST" },
            ]
          : [{ type: "Drivers", id: "LIST" }],
    }),

    /**
     * Get a single driver by ID
     * @param {string} id - Driver user ID
     * @returns Driver object with populated fields
     */
    getDriver: builder.query({
      query: (id) => `users/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Drivers", id }],
    }),

    /**
     * Create a new driver
     * @param {Object} data - Driver data
     * @param {string} data.firstName - Driver's first name
     * @param {string} data.lastName - Driver's last name
     * @param {string} data.type - Must be "driver"
     * @param {string} data.email - Driver's email (optional)
     * @param {string} data.phone - Driver's phone (optional)
     * @param {Object} data.meta - Driver metadata (license, rates, etc.)
     * @returns Created driver object
     */
    createDriver: builder.mutation({
      query: (data) => ({
        url: "users",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "Drivers", id: "LIST" },
        { type: "Users", id: "LIST" },
      ],
    }),

    /**
     * Update an existing driver
     * @param {Object} params - Update parameters
     * @param {string} params.id - Driver user ID
     * @param {Object} params.data - Updated driver data
     * @returns Updated driver object
     */
    updateDriver: builder.mutation({
      query: ({ id, data }) => ({
        url: `users/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Drivers", id },
        { type: "Drivers", id: "LIST" },
        { type: "Users", id },
      ],
    }),

    /**
     * Delete one or more drivers
     * @param {string[]} ids - Array of driver IDs to delete
     * @returns Deletion result
     */
    deleteDrivers: builder.mutation({
      query: (ids) => ({
        url: "drivers/delete",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: [{ type: "Drivers", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDriversQuery,
  useGetDriverQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriversMutation,
} = driversApi;
